import React, { useCallback, useEffect, useState } from "react";
import { getManagedRoles, isManager } from "data/app/appSelectors";
import { useSelector } from "react-redux";
import queryString from "query-string";
import { Redirect } from "react-router-dom";
import TasksManagerViewHeader from "./TasksManagerViewHeader";
import api from "lib/api";
import TasksManagerTable from "./TasksManagerTable";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import axios from "axios";
import ReassignDialog from "components/ReassignDialog";
import { Loading, SearchSelectOptionProps } from "@alm/flow-ds/components";
import { useTranslation } from "react-i18next";

const TasksManagerView = () => {
	const { t } = useTranslation();

	const manager = useSelector(isManager);
	const managedRoles = useSelector(getManagedRoles);

	const [searchValue, setSearchValue] = useState("");
	const [statusSelect, setStatusSelect] = useState("ACTIVE");
	const [assigneeSelect, setAssigneeSelect] = useState("ALL");

	const [candidateSelect, setCandidateSelect] =
		useState<SearchSelectOptionProps>({
			label: t("main.tasks.headings.allRoles"),
			value: "ALL_ROLES",
		});

	const [managerState, setManagerState] = useState<{
		roles: string[];
		users: string[];
	}>({ roles: [], users: [] });

	const { _isMounted } = useCheckMountedStatus();
	const [init, setInit] = useState(false);

	const { assignId }: { assignId?: string } = queryString.parse(
		window.location.search
	);

	const getRolesAndManagers = useCallback(async () => {
		const tmpState: { roles: string[]; users: string[] } = {
			roles: [],
			users: [],
		};

		tmpState.roles = Object.keys(managedRoles);

		const setUsersForCurrentManagerRoles = async () => {
			try {
				const res = await api.roles.getUsersForManagerRoles();
				// Only update state when the component is mounted
				if (_isMounted.current) {
					tmpState.users = res;
				}
			} catch (error) {
				if (error && axios.isAxiosError(error))
					console.error(`Error: ${error.message}`);
			}
		};
		await setUsersForCurrentManagerRoles();
		setManagerState(tmpState);
		setInit(true);
	}, [_isMounted, managedRoles]);

	useEffect(() => {
		if (!init) {
			getRolesAndManagers();
		}
	}, [_isMounted, getRolesAndManagers, init]);

	if (!init) return <Loading className="mt-4 elem__loader--md" />;

	return (
		<div className="container tasks">
			{manager ? (
				<>
					<div className="list-group-fixed-header">
						<TasksManagerViewHeader
							allManagerRoles={managerState.roles}
							allManagerUsers={managerState.users}
							status={statusSelect}
							candidate={candidateSelect}
							assignee={assigneeSelect}
							onChangeSearchValue={setSearchValue}
							onChangeStatusValue={setStatusSelect}
							onChangeCandidateValue={setCandidateSelect}
							onChangeAssigneeValue={setAssigneeSelect}
						/>
					</div>

					<TasksManagerTable
						search={searchValue}
						status={statusSelect as "ACTIVE" | "COMPLETED" | "ALL"}
						selectedCandidate={candidateSelect}
						allManagerRoles={managerState.roles}
						allUserRoles={managerState.users}
						selectedAssignee={assigneeSelect}
						setStatusValue={setStatusSelect}
						setCandidateValue={setCandidateSelect}
						setAssigneeValue={setAssigneeSelect}
						requestTasks={api.workflow.getTasksAsManager}
					/>
				</>
			) : (
				<Redirect to="/403" />
			)}
			{assignId && <ReassignDialog id={assignId} />}
		</div>
	);
};

export default TasksManagerView;
